/* Drink Nature brand pages (sections/dn-loc-carte.liquid, dn-prevente-coffret.liquid, dn-prevente-annonce.liquid).
   Vanilla custom elements, loaded with defer. Custom elements upgrade again by themselves when the theme editor
   re-renders a section, so nothing here keeps state outside the element. Safe to load from several sections.
   No text lives here: every label comes from the section's settings. */
(() => {
  if (window.DnMarqueLoaded) return;
  window.DnMarqueLoaded = true;

  /* Lower case, no accents, hyphens and apostrophes as spaces: "Saint-Félicien" matches "saint felicien". */
  const normaliser = (texte) =>
    (texte || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[’'’\-–—,.()]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  /* ---------- <dn-localisateur>: text filter and quick filters over the retailer list ---------- */
  class DnLocalisateur extends HTMLElement {
    connectedCallback() {
      this.champ = this.querySelector('[data-dn-recherche]');
      this.formulaire = this.querySelector('[data-dn-formulaire]');
      this.filtres = [...this.querySelectorAll('[data-dn-filtre]')];
      this.magasins = [...this.querySelectorAll('[data-dn-magasin]')].map((element) => ({
        element,
        texte: normaliser(element.dataset.dnMagasin),
      }));
      this.compte = this.querySelector('[data-dn-compte]');
      this.vide = this.querySelector('[data-dn-vide]');
      this.filtre = '';

      this.onInput = () => this.appliquer();
      this.onSubmit = (event) => {
        event.preventDefault();
        this.appliquer();
      };
      this.onClick = (event) => {
        const bouton = event.target.closest('[data-dn-filtre]');
        if (!bouton || !this.contains(bouton)) return;
        this.filtre = normaliser(bouton.dataset.dnFiltre);
        this.filtres.forEach((autre) => autre.setAttribute('aria-pressed', autre === bouton ? 'true' : 'false'));
        this.appliquer();
      };

      this.champ?.addEventListener('input', this.onInput);
      this.formulaire?.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.appliquer();
    }

    disconnectedCallback() {
      this.champ?.removeEventListener('input', this.onInput);
      this.formulaire?.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onClick);
    }

    appliquer() {
      const mots = normaliser(this.champ?.value).split(' ').filter(Boolean);
      // A quick filter matches whole words ("IGA" must not keep "Shawinigan"); typed words may be partial.
      const filtre = this.filtre ? ' ' + this.filtre + ' ' : '';
      let visibles = 0;
      this.magasins.forEach(({ element, texte }) => {
        const garde = (!filtre || (' ' + texte + ' ').includes(filtre)) && mots.every((mot) => texte.includes(mot));
        element.hidden = !garde;
        if (garde) visibles += 1;
      });
      if (this.compte) {
        const modele = (visibles > 1 ? this.dataset.plusieurs : this.dataset.un) || '[n]';
        this.compte.textContent = modele.replace('[n]', visibles);
      }
      if (this.vide) this.vide.hidden = visibles !== 0;
    }
  }

  /* ---------- <dn-coffret>: pick bags, see the count and the total, add them to the cart ----------
     Only active in the section's "choix des sacs sur la page" mode (the config script is printed in that mode only).
     Adds through window.EcCartApi (assets/ecom-cart.js, loaded with the cart drawer): one POST /cart/add.js with
     every chosen bag, then the drawer opens. Without the drawer it posts to /cart/add.js itself and goes to the
     cart. A request is never repeated automatically. */
  const formateurMonnaie = (exemple) => {
    // exemple = 123456789 cents formatted by Shopify (1,234,567.89 in the shop's own format)
    const m = typeof exemple === 'string' ? exemple.match(/1(\D?)234(\D?)567(\D?)89/) : null;
    if (!m) return (cents) => (cents / 100).toFixed(2);
    const avant = exemple.slice(0, m.index);
    const apres = exemple.slice(m.index + m[0].length);
    const milliers = m[1];
    const decimale = m[3];
    return (cents) => {
      const valeur = Math.round(Math.abs(cents));
      const entier = Math.floor(valeur / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, milliers);
      const fraction = String(valeur % 100).padStart(2, '0');
      return avant + entier + decimale + fraction + apres;
    };
  };

  class DnCoffret extends HTMLElement {
    connectedCallback() {
      const source = this.querySelector('[data-dn-config]');
      this.barre = this.querySelector('[data-dn-barre]');
      if (!source || !this.barre) return;
      try {
        this.config = JSON.parse(source.textContent || '{}');
      } catch (erreur) {
        return;
      }
      this.textes = this.config.textes || {};
      this.seuil = Math.max(1, Number(this.config.seuil) || 3);
      this.rabais = Math.max(0, Number(this.config.rabais) || 0);
      this.monnaie = formateurMonnaie(this.config.monnaie);
      this.saveurs = [...this.querySelectorAll('[data-dn-saveur]')]
        .map((element) => ({
          element,
          id: Number(element.dataset.variantId) || 0,
          prix: Number(element.dataset.prix) || 0,
          quantite: Math.max(0, Number(element.dataset.depart) || 0),
          moins: element.querySelector('[data-dn-moins]'),
          plus: element.querySelector('[data-dn-plus]'),
          affichage: element.querySelector('[data-dn-qte]'),
        }))
        .filter((saveur) => saveur.id && saveur.plus);
      if (!this.saveurs.length) return;

      this.compte = this.barre.querySelector('[data-dn-compte]');
      this.statut = this.barre.querySelector('[data-dn-statut]');
      this.jauge = this.barre.querySelector('[data-dn-jauge]');
      this.avant = this.barre.querySelector('[data-dn-avant]');
      this.total = this.barre.querySelector('[data-dn-total]');
      this.bouton = this.barre.querySelector('[data-dn-ajouter]');
      this.erreur = this.barre.querySelector('[data-dn-erreur]');
      this.ajoute = false;
      this.occupe = false;

      this.onClick = (event) => {
        const cible = event.target.closest('[data-dn-moins],[data-dn-plus],[data-dn-ajouter]');
        if (!cible || !this.contains(cible)) return;
        if (cible.hasAttribute('data-dn-ajouter')) {
          this.ajouter();
          return;
        }
        const saveur = this.saveurs.find((s) => s.element.contains(cible));
        if (!saveur || this.occupe) return;
        const pas = cible.hasAttribute('data-dn-plus') ? 1 : -1;
        saveur.quantite = Math.max(0, Math.min(99, saveur.quantite + pas));
        this.ajoute = false;
        this.afficherErreur('');
        this.rendre();
      };
      this.addEventListener('click', this.onClick);
      this.rendre();
    }

    disconnectedCallback() {
      if (this.onClick) this.removeEventListener('click', this.onClick);
    }

    nombre() {
      return this.saveurs.reduce((somme, saveur) => somme + saveur.quantite, 0);
    }

    remplir(modele, reste) {
      return (modele || '')
        .replace('[reste]', reste)
        .replace('[rabais]', this.rabais)
        .replace('[seuil]', this.seuil);
    }

    rendre() {
      const t = this.textes;
      const n = this.nombre();
      const atteint = n >= this.seuil;
      const reste = Math.max(0, this.seuil - n);

      this.saveurs.forEach((saveur) => {
        saveur.element.classList.toggle('is-choisi', saveur.quantite > 0);
        if (saveur.affichage) saveur.affichage.textContent = saveur.quantite;
        if (saveur.moins) saveur.moins.disabled = saveur.quantite === 0;
      });

      if (this.compte) {
        let texte = Math.min(n, this.seuil) + ' / ' + this.seuil;
        if (n > this.seuil) texte += ' +' + (n - this.seuil);
        if (!atteint) {
          const mot = n > 1 ? t.mots : t.mot;
          if (mot) texte += ' ' + mot;
        }
        this.compte.textContent = texte;
      }
      if (this.statut) {
        let modele = reste > 1 ? t.restes : t.reste;
        if (atteint) modele = t.actif;
        if (this.ajoute) modele = t.ajoute;
        this.statut.textContent = this.remplir(modele, reste);
      }
      if (this.jauge) this.jauge.style.width = Math.min(100, (n / this.seuil) * 100) + '%';

      // Same arithmetic as Shopify's automatic discount: per bag, the discount amount rounded down to the cent.
      const sousTotal = this.saveurs.reduce((somme, s) => somme + s.prix * s.quantite, 0);
      const avecRabais = atteint && this.rabais > 0 && this.config.afficherRabais;
      const total = avecRabais
        ? this.saveurs.reduce((somme, s) => somme + (s.prix - Math.floor((s.prix * this.rabais) / 100)) * s.quantite, 0)
        : sousTotal;
      if (this.total) this.total.textContent = this.monnaie(total);
      if (this.avant) {
        this.avant.hidden = !avecRabais || sousTotal <= total;
        this.avant.textContent = this.avant.hidden ? '' : this.monnaie(sousTotal);
      }

      this.barre.classList.toggle('is-actif', atteint || this.ajoute);
      if (this.bouton) {
        this.bouton.textContent = this.remplir(atteint ? t.boutonActif : t.boutonInactif, reste);
        this.bouton.setAttribute('aria-disabled', atteint ? 'false' : 'true');
      }
    }

    afficherErreur(message) {
      if (!this.erreur) return;
      this.erreur.textContent = message || '';
      this.erreur.hidden = !message;
    }

    async ajouter() {
      if (this.occupe || this.nombre() < this.seuil) return;
      const items = this.saveurs.filter((s) => s.quantite > 0).map((s) => ({ id: s.id, quantity: s.quantite }));
      if (!items.length) return;
      this.occupe = true;
      this.bouton?.setAttribute('aria-busy', 'true');
      this.afficherErreur('');
      try {
        if (window.EcCartApi) {
          await window.EcCartApi.add(items);
        } else {
          const racine = window.Shopify?.routes?.root || '/';
          const reponse = await fetch(racine + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const resultat = await reponse.json().catch(() => ({}));
          if (!reponse.ok || resultat.status) throw new Error(resultat.description || resultat.message || '');
          window.location.assign(racine + 'cart');
        }
        this.ajoute = true;
        this.rendre();
      } catch (erreur) {
        this.afficherErreur((erreur && erreur.message) || this.textes.erreur || '');
      } finally {
        this.occupe = false;
        this.bouton?.removeAttribute('aria-busy');
      }
    }
  }

  /* ---------- [data-dn-fin]: hide an element from a given moment (the pre-sale announcement line) ----------
     data-dn-fin = "AAAA-MM-JJ HH:MM" read in the time zone of data-dn-fuseau (the store's). Never hides in the
     theme editor, so the merchant can still edit the section. */
  const instantDansFuseau = (texte, fuseau) => {
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2})[:h](\d{2}))?$/.exec((texte || '').trim());
    if (!m) return null;
    const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0));
    try {
      const format = new Intl.DateTimeFormat('en-US', {
        timeZone: fuseau || 'America/Toronto',
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      });
      const p = {};
      format.formatToParts(new Date(utc)).forEach((partie) => {
        p[partie.type] = Number(partie.value);
      });
      const vuDansFuseau = Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second);
      return utc - (vuDansFuseau - utc);
    } catch (erreur) {
      return utc;
    }
  };

  const masquerEchus = (racine) => {
    if (window.Shopify && window.Shopify.designMode) return;
    (racine || document).querySelectorAll('[data-dn-fin]').forEach((element) => {
      const fin = instantDansFuseau(element.dataset.dnFin, element.dataset.dnFuseau);
      if (fin !== null && Date.now() >= fin) element.hidden = true;
    });
  };

  const definir = (nom, classe) => {
    if (!window.customElements.get(nom)) window.customElements.define(nom, classe);
  };
  definir('dn-localisateur', DnLocalisateur);
  definir('dn-coffret', DnCoffret);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => masquerEchus());
  else masquerEchus();
})();
